import {useFormikContext} from 'formik';
import React, {useCallback, useEffect, useState} from 'react';
import {Image} from 'react-bootstrap';
import {DrawerFields} from 'components/customer/Product/entity/Drawer';
import {useRunnerRequestParams} from 'components/customer/Product/Drawer/helpers/useRunnerRequestParams';
import {useGetRunnerQuery} from 'components/customer/Product/store/drawerRunnerApi';
import {runnerIdsMatch} from 'components/customer/Product/Drawer/helpers';

interface DrawerThumbnailInterfaec {
    runnerIndex: number;
    drawerTypeFieldName: string;
    drawerFaceHeightFieldName: string;
    fingerPullFieldName: string;
    drawerDepth: string;
    // replaces the default class, so a caller can style it without fighting
    // the .drawer-thumbnail rule in product.scss
    className?: string;
    // shown when the runner has no picture, rather than nothing at all
    fallback?: React.ReactNode;
}

export const DrawerThumbnail = ({
    runnerIndex,
    drawerTypeFieldName,
    drawerFaceHeightFieldName,
    fingerPullFieldName,
    drawerDepth,
    className = 'drawer-thumbnail',
    fallback = null,
}: DrawerThumbnailInterfaec) => {
    const {values} = useFormikContext<DrawerFields>();
    const {params} = useRunnerRequestParams(
        runnerIndex,
        false,
        // isDrawerComponent: omitting it shifted every argument after this one,
        // which left the query skipped and the thumbnail blank
        false,
        drawerTypeFieldName,
        drawerDepth,
        drawerFaceHeightFieldName,
        fingerPullFieldName
    );

    const {runner} = useGetRunnerQuery(params, {
        selectFromResult: ({data}) => ({
            runner: data?.find((runner) =>
                runnerIdsMatch(
                    runner.id,
                    // eslint-disable-next-line security/detect-object-injection -- numeric index, safe
                    values.drawers[runnerIndex].drawer_runner_specs
                )
            ),
        }),
        skip:
            params.insertType == null ||
            params.insertType == 0 ||
            params.faceHeight <= -1,
    });

    // a name in the record is no guarantee the file is there, so a failed load
    // falls back the same way a missing name does
    const [broken, setBroken] = useState(false);
    const failHandler = useCallback(() => setBroken(true), []);

    useEffect(() => setBroken(false), [runner?.image]);

    if (
        typeof runner === 'undefined' ||
        (runner && runner.imageName == null) ||
        broken
    ) {
        return fallback as JSX.Element;
    }

    return (
        <section className={className}>
            <Image src={`${runner.image}`} rounded onError={failHandler} />
        </section>
    );
};
